import { Link } from 'react-router-dom';
import PageMeta from '../../components/SEO/PageMeta';
import SiteFooter from '../../components/Footer/SiteFooter';
import SiteNavigation from '../../components/Navigation/SiteNavigation';
import './Applications.css';

const applications = [
  {
    name: 'Atlas Studio',
    category: 'Creator tools',
    status: 'Planned application',
    image: '/artwork/applications/atlas-studio.webp',
    description: 'Edit video, audio, images, and social content in one clear production workspace.',
    emphasis: 'Featured creator application'
  },
  {
    name: 'Atlas Docs',
    category: 'Documents',
    status: 'Planned application',
    image: '/artwork/applications/atlas-docs.webp',
    description: 'Write and edit documents that stay portable and connected to your Atlas projects.',
    emphasis: 'Writing without unnecessary complexity'
  },
  {
    name: 'Atlas Sheets',
    category: 'Spreadsheets',
    status: 'Planned application',
    image: '/artwork/applications/atlas-sheets.webp',
    description: 'Create spreadsheets for calculations, planning, analysis, and everyday business work.',
    emphasis: 'Clear data, connected work'
  },
  {
    name: 'Atlas Books',
    category: 'Small business accounting',
    status: 'Planned application',
    image: '/artwork/applications/atlas-books.webp',
    description: 'Manage small-business bookkeeping with a clear workflow and practical guidance.',
    emphasis: 'Bookkeeping built for real operators'
  },
  {
    name: 'Atlas Research',
    category: 'Research and knowledge',
    status: 'Planned application',
    image: '/artwork/applications/atlas-research.webp',
    description: 'Collect sources, organize findings, trace information, and keep the evidence behind your work.',
    emphasis: 'From scattered sources to clear understanding'
  }
];

export default function Applications() {
  const applicationHeroStyle = {
    backgroundImage: `linear-gradient(180deg, rgba(4, 10, 20, 0.2), rgba(4, 10, 20, 0.72)), url(${process.env.PUBLIC_URL}/artwork/applications/atlas-studio.webp)`
  };

  return (
    <>
      <PageMeta
        title="Future Atlas Applications | Atlas Creative Labs"
        description="Preview planned Atlas applications that may later work with the same organized files and projects. Current development remains focused on the main Atlas desktop app."
        path="/applications"
        image="/artwork/applications/atlas-studio.webp"
      />
      <main className="applications-page">
        <section className="applications-hero" style={applicationHeroStyle}>
          <SiteNavigation />
          <div className="page-shell applications-hero__content">
            <p className="section-eyebrow">Future Atlas applications</p>
            <h1>One organized home, with focused tools for different jobs.</h1>
            <p className="applications-hero__lead">
              Atlas is the central product. Future tools for media, documents, spreadsheets, bookkeeping, and research are planned to work with the same organized files and projects.
            </p>
            <div className="applications-hero__actions">
              <Link className="button button--primary" to="/atlas">Explore Atlas</Link>
              <Link className="button button--secondary" to="/pre-alpha">Join Pre-Alpha</Link>
            </div>
          </div>
        </section>

        <section className="content-section">
          <div className="page-shell">
            <div className="section-heading">
              <p className="section-eyebrow">Future application previews</p>
              <h2>Familiar tools planned to share the same files and project context.</h2>
              <p className="section-heading__summary">
                These previews show the intended direction of the Atlas ecosystem. They are not products currently available for download or purchase.
              </p>
            </div>

            <div className="public-app-grid">
              {applications.map((application, index) => (
                <article className={`public-app-card${index === 0 ? ' public-app-card--featured' : ''}`} key={application.name}>
                  <div className="public-app-card__image-wrap">
                    <img className="public-app-card__image" src={application.image} alt={`${application.name} planned application preview`} />
                  </div>
                  <div className="public-app-card__content">
                    <div className="public-app-card__header">
                      <div>
                        <p className="public-app-card__category">{application.category}</p>
                        <h3>{application.name}</h3>
                      </div>
                      <span className="public-app-card__status">{application.status}</span>
                    </div>
                    <p className="public-app-card__description">{application.description}</p>
                    <p className="public-app-card__emphasis">{application.emphasis}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="content-section content-section--soft">
          <div className="page-shell applications-boundary">
            <div>
              <p className="section-eyebrow">Built in stages</p>
              <h2>Atlas comes first. Additional applications will follow when they are ready.</h2>
            </div>
            <div className="body-copy">
              <p>Current development is focused on the central Atlas desktop experience.</p>
              <p>Each future application will be introduced only as its design, testing, and release plan become ready.</p>
              <p>This page shows future direction, not a list of products currently available.</p>
            </div>
          </div>
        </section>

        <section className="content-section content-section--closing">
          <div className="page-shell closing-panel">
            <div>
              <p className="section-eyebrow">Start with Atlas</p>
              <h2>See how Atlas organizes files across folders and drives.</h2>
              <p>The main Atlas app is the foundation. Explore how it creates one centralized workspace without moving the original files.</p>
            </div>
            <Link className="button button--primary" to="/atlas">Explore Atlas</Link>
          </div>
        </section>

        <SiteFooter />
      </main>
    </>
  );
}
